import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Globe, ChevronRight, Clock, ExternalLink, RefreshCw, TrendingUp, Cpu, Beaker, Dna, LineChart, Menu, X, Bell, Mail, Check, AlertCircle, ScrollText, Calendar, Landmark, Play, ChevronLeft, FileDown } from "lucide-react";
import { Toaster, toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { useSeo, articleJsonLd, TAGLINE, SITE_DESCRIPTION } from "./seo";
import { SupplyChainMap } from "./SupplyChainMap";
import { LabourShareChart } from "./LabourShareChart";
import { WageProductivityChart } from "./WageProductivityChart";
import { IncomeSharesChart } from "./IncomeSharesChart";
import { IncomeDecilesChart } from "./IncomeDecilesChart";
import { ManufacturingShareChart } from "./ManufacturingShareChart";
import { EditorialCharts, isLabourFeature } from "./EditorialCharts";
import { DataOfTheDay } from "./DataOfTheDay";
import { MERTON_CHARTS } from "./MertonCharts";
import { DIVERGENCE_CHARTS } from "./DivergenceCharts";
import { RUSTBELT_CHARTS } from "./RustBeltCharts";
import { ECONOMETRICS_CHARTS } from "./EconometricsCharts";

const isChipFeature = (tags) => (tags || []).includes('editorial_feature') && (tags || []).includes('supply_chain');

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language context
const translations = {
  en: {
    breaking: "BREAKING",
    latest: "Latest News",
    search: "Search articles...",
    categories: "Categories",
    allNews: "All News",
    readMore: "Read more",
    source: "Source",
    updated: "Updated",
    version: "Version",
    noResults: "No articles found",
    loading: "Loading...",
    viewAll: "View all",
    home: "Home",
    about: "About",
    searchResults: "Search Results",
    resultsFor: "Results for",
    backToHome: "Back to Home",
    refresh: "Refresh",
    scraping: "Fetching news...",
    totalArticles: "Total Articles",
    lastUpdate: "Last Update",
    subscribe: "Subscribe to Newsletter",
    email: "Email",
    name: "Name (optional)",
    selectCategories: "Select categories (leave empty for all)",
    subscribeBtn: "Subscribe",
    subscribing: "Subscribing...",
    subscribeSuccess: "Successfully subscribed!",
    subscribeError: "Subscription failed",
    notifications: "Notifications",
    noNotifications: "No new notifications",
    markAllRead: "Mark all as read",
    newBreaking: "New Breaking News",
    timeline: "Timeline",
    timelineTitle: "Economic History Timeline",
    timelineDesc: "Explore economic events across historical periods",
    allPeriods: "All Periods",
    articlesInPeriod: "articles",
    year: "Year",
    relatedTheories: "Related economic theories",
    relatedTheoriesDesc: "From the facts to economic thought",
  },
  it: {
    breaking: "ULTIME",
    latest: "Ultime Notizie",
    search: "Cerca articoli...",
    categories: "Categorie",
    allNews: "Tutte le Notizie",
    readMore: "Leggi di più",
    source: "Fonte",
    updated: "Aggiornato",
    version: "Versione",
    noResults: "Nessun articolo trovato",
    loading: "Caricamento...",
    viewAll: "Vedi tutti",
    home: "Home",
    about: "Chi siamo",
    searchResults: "Risultati Ricerca",
    resultsFor: "Risultati per",
    backToHome: "Torna alla Home",
    refresh: "Aggiorna",
    scraping: "Recupero notizie...",
    totalArticles: "Articoli Totali",
    lastUpdate: "Ultimo Aggiornamento",
    subscribe: "Iscriviti alla Newsletter",
    email: "Email",
    name: "Nome (opzionale)",
    selectCategories: "Seleziona categorie (lascia vuoto per tutte)",
    subscribeBtn: "Iscriviti",
    subscribing: "Iscrizione...",
    subscribeSuccess: "Iscrizione completata!",
    subscribeError: "Iscrizione fallita",
    notifications: "Notifiche",
    noNotifications: "Nessuna nuova notifica",
    markAllRead: "Segna tutte come lette",
    newBreaking: "Nuova Notizia Urgente",
    timeline: "Timeline",
    timelineTitle: "Linea del Tempo Storia Economica",
    timelineDesc: "Esplora gli eventi economici attraverso i periodi storici",
    allPeriods: "Tutti i Periodi",
    articlesInPeriod: "articoli",
    year: "Anno",
    relatedTheories: "Teorie economiche collegate",
    relatedTheoriesDesc: "Dai fatti al pensiero economico",
  }
};

const categoryIcons = {
  science: Beaker,
  biology: Dna,
  technology: Cpu,
  hardware: Cpu,
  economics: LineChart,
  finance: Landmark,
  economic_history: ScrollText,
};

const categoryColors = {
  science: "#1a56db",
  biology: "#047857",
  technology: "#7c3aed",
  hardware: "#dc2626",
  economics: "#d97706",
  finance: "#0f766e",
  economic_history: "#78350f",
};

// Notification Bell Component
const NotificationBell = ({ lang }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const t = translations[lang];

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`);
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Show toast for new notifications
  useEffect(() => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length > 0) {
      const latest = unreadNotifications[0];
      if (!sessionStorage.getItem(`notified_${latest.id}`)) {
        toast(t.newBreaking, {
          description: (lang === 'it' && latest.title_it) || latest.title,
          action: {
            label: t.readMore,
            onClick: () => window.location.href = `/${lang}/article/${latest.article_id}`
          },
        });
        sessionStorage.setItem(`notified_${latest.id}`, 'true');
      }
    }
  }, [notifications, t]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking notifications read:", err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000 / 60; // minutes
    
    if (diff < 60) return `${Math.floor(diff)}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };

  return (
    <div className="notification-bell" data-testid="notification-bell">
      <button 
        className="bell-btn"
        onClick={() => setShowDropdown(!showDropdown)}
        data-testid="notification-btn"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="notification-badge" data-testid="notification-badge">{unreadCount}</span>
        )}
      </button>
      
      {showDropdown && (
        <div className="notification-dropdown" data-testid="notification-dropdown">
          <div className="notification-header">
            <span>{t.notifications}</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="mark-read-btn">
                <Check size={14} /> {t.markAllRead}
              </button>
            )}
          </div>
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">{t.noNotifications}</div>
            ) : (
              notifications.map(notification => (
                <Link
                  key={notification.id}
                  to={`/${lang}/article/${notification.article_id}`}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => setShowDropdown(false)}
                >
                  <div className="notification-category" style={{ color: categoryColors[notification.category] }}>
                    {notification.category?.toUpperCase()}
                  </div>
                  <div className="notification-title">{(lang === 'it' && notification.title_it) || notification.title}</div>
                  <div className="notification-time">{formatTime(notification.created_at)}</div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Newsletter Subscription Component
const NL = {
  it: {
    name: 'Il Brief delle 7',
    tagline: 'Le sette notizie di scienza, tecnologia ed economia che contano, ogni mattina alle 7. Tre minuti di lettura, zero rumore.',
    cta: 'Iscriviti gratis',
    placeholder: 'La tua email',
    pending: 'Controlla la posta: ti abbiamo inviato un link per confermare l\'iscrizione.',
    already: 'Questo indirizzo è già iscritto.',
    recently: 'Ti abbiamo già inviato il link di conferma pochi minuti fa: controlla anche lo spam.',
    error: 'Iscrizione non riuscita, riprova tra poco.',
    confirmed: 'Iscrizione confermata! Da domani alle 7 riceverai Il Brief delle 7.',
    unsubscribed: 'Iscrizione annullata. Ci dispiace vederti andare via.',
    invalid: 'Link non valido o già usato.',
    privacy: 'Doppia conferma via email. Nessuna cessione a terzi, puoi annullare con un clic da ogni numero.',
    preview: 'Anteprima del numero di oggi',
    langLabel: 'Lingua del Brief',
    howTitle: 'Come funziona',
    how: ['Ogni giorno l\'AI di Horizon Brief legge centinaia di fonti primarie: università, istituti di ricerca, banche centrali, aziende tech.', 'Alle 7 ricevi le sette notizie più importanti, una per sezione quando possibile, con un paragrafo "Perché conta".', 'Ogni notizia rimanda all\'articolo completo su horizonbrief.it, leggibile in 8 lingue.'],
    articleCta: 'Ricevi notizie come questa ogni mattina alle 7',
  },
  en: {
    name: "The 7 O'Clock Brief",
    tagline: 'The seven science, technology and economics stories that matter, every morning at 7. Three minutes, no noise.',
    cta: 'Subscribe for free',
    placeholder: 'Your email',
    pending: 'Check your inbox: we sent you a link to confirm your subscription.',
    already: 'This address is already subscribed.',
    recently: 'We sent you the confirmation link a few minutes ago: please check your spam folder too.',
    error: 'Subscription failed, please try again shortly.',
    confirmed: "Subscription confirmed! From tomorrow at 7 you will receive The 7 O'Clock Brief.",
    unsubscribed: 'You have been unsubscribed. Sorry to see you go.',
    invalid: 'Invalid or already used link.',
    privacy: 'Double opt-in by email. Never shared with third parties; unsubscribe with one click from any issue.',
    preview: "Preview of today's issue",
    langLabel: 'Brief language',
    howTitle: 'How it works',
    how: ["Every day Horizon Brief's AI reads hundreds of primary sources: universities, research institutes, central banks, tech companies.", 'At 7 you get the seven most important stories, one per section when possible, each with a "Why it matters" paragraph.', 'Every story links to the full article on horizonbrief.it, readable in 8 languages.'],
    articleCta: 'Get stories like this every morning at 7',
  },
};

const NewsletterForm = ({ lang, compact = false, source = 'sidebar' }) => {
  const [email, setEmail] = useState("");
  const [nlLang, setNlLang] = useState(lang);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(null);
  const n = NL[lang] || NL.it;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email, lang: nlLang, source });
      const st = res.data.status === 'pending' && res.data.message === 'confirmation_recently_sent' ? 'recently' : res.data.status;
      setState(st);
      if (st === 'pending') setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || n.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'pending' || state === 'already_confirmed' || state === 'recently') {
    return (
      <div className={`newsletter-success ${compact ? 'compact' : ''}`} data-testid="newsletter-success">
        <Check size={28} />
        <p>{state === 'pending' ? n.pending : state === 'recently' ? n.recently : n.already}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`newsletter-form ${compact ? 'compact' : ''}`} data-testid="newsletter-form">
      {!compact && <h3><Mail size={18} /> {n.name}</h3>}
      {!compact && <p className="newsletter-tagline">{n.tagline}</p>}
      <div className="newsletter-row">
        <input
          type="email"
          placeholder={n.placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="newsletter-email"
        />
        <button type="submit" className="subscribe-btn" disabled={submitting} data-testid="newsletter-submit">
          {submitting ? '…' : n.cta}
        </button>
      </div>
      <div className="newsletter-lang" role="radiogroup" aria-label={n.langLabel}>
        {['it', 'en'].map(l => (
          <label key={l} className={`newsletter-lang-opt ${nlLang === l ? 'active' : ''}`}>
            <input type="radio" name={`nl-lang-${source}`} value={l} checked={nlLang === l} onChange={() => setNlLang(l)} data-testid={`newsletter-lang-${l}`} />
            {l === 'it' ? 'Italiano' : 'English'}
          </label>
        ))}
      </div>
      <p className="newsletter-privacy">{n.privacy}</p>
    </form>
  );
};

// Newsletter landing page
const NewsletterPage = ({ lang }) => {
  const [searchParams] = useSearchParams();
  const [previewHtml, setPreviewHtml] = useState("");
  const n = NL[lang] || NL.it;
  const notice = searchParams.get('confirmed') ? n.confirmed : searchParams.get('unsubscribed') ? n.unsubscribed : searchParams.get('error') ? n.invalid : null;
  useSeo({ title: n.name, path: '/newsletter', lang, description: n.tagline });

  useEffect(() => {
    axios.get(`${API}/newsletter/preview?lang=${lang}`).then(r => setPreviewHtml(r.data)).catch(() => setPreviewHtml(""));
  }, [lang]);

  return (
    <div className="newsletter-page" data-testid="newsletter-page">
      {notice && (
        <div className={`newsletter-notice ${searchParams.get('error') ? 'error' : ''}`} data-testid="newsletter-notice">{notice}</div>
      )}
      <div className="newsletter-hero">
        <span className="newsletter-kicker">{lang === 'it' ? 'Newsletter quotidiana' : 'Daily newsletter'}</span>
        <h1>{n.name}</h1>
        <p className="newsletter-hero-tagline">{n.tagline}</p>
        <NewsletterForm lang={lang} source="newsletter-page" />
      </div>
      <div className="newsletter-grid">
        <section className="newsletter-how">
          <h2>{n.howTitle}</h2>
          <ol>{n.how.map((h, i) => <li key={i}>{h}</li>)}</ol>
        </section>
        <section className="newsletter-preview">
          <h2>{n.preview}</h2>
          {previewHtml
            ? <div className="newsletter-preview-frame" data-testid="newsletter-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            : <p className="newsletter-preview-empty">…</p>}
        </section>
      </div>
    </div>
  );
};

// Institutional video wall (YouTube channel feeds, privacy-enhanced embed on click)
const VideoWall = ({ lang }) => {
  const [videos, setVideos] = useState([]);
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    axios.get(`${API}/videos?limit=8`).then(r => setVideos(r.data.videos || [])).catch(() => setVideos([]));
  }, []);
  if (videos.length === 0) return null;
  const fmt = (iso) => new Date(iso).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', { day: 'numeric', month: 'short' });
  return (
    <section className="video-wall" data-testid="video-wall">
      <div className="section-header">
        <h2 className="section-title"><Play size={18} /> {lang === 'it' ? 'Video dalle istituzioni' : 'Video from the institutions'}</h2>
        <span className="video-wall-sub">{lang === 'it' ? 'Canali ufficiali: NASA, ESA, CERN, MIT, BCE, Fed…' : 'Official channels: NASA, ESA, CERN, MIT, ECB, Fed…'}</span>
      </div>
      <div className="video-grid">
        {videos.map(v => (
          <article key={v.id} className={`video-card ${playing === v.id ? 'playing' : ''}`} data-testid={`video-card-${v.id}`}>
            {playing === v.id ? (
              <div className="video-frame">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`}
                  title={v.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <button type="button" className="video-thumb" onClick={() => setPlaying(v.id)} data-testid={`video-play-${v.id}`} aria-label={v.title}>
                <img src={v.thumbnail} alt="" loading="lazy" />
                <span className="video-play-icon"><Play size={22} fill="currentColor" /></span>
              </button>
            )}
            <div className="video-meta">
              <span className="video-channel" style={{ color: categoryColors[v.category] || '#003366' }}>{v.channel}</span>
              <span className="video-date">{fmt(v.published_at)}</span>
            </div>
            <h3 className="video-title">{v.title}</h3>
          </article>
        ))}
      </div>
    </section>
  );
};

// Guided reading paths (e.g. "Storia economica d'Italia")
const useReadingPaths = () => {
  const [paths, setPaths] = useState([]);
  useEffect(() => {
    axios.get(`${API}/timeline/paths`).then(r => setPaths(r.data.paths || [])).catch(() => setPaths([]));
  }, []);
  return paths;
};

const ReadingPaths = ({ lang }) => {
  const paths = useReadingPaths();
  const ready = paths.filter(p => p.steps.length > 0);
  if (ready.length === 0) return null;
  return (
    <section className="reading-paths" data-testid="reading-paths">
      <div className="reading-paths-kicker">{lang === 'it' ? 'Percorsi di lettura' : 'Reading paths'}</div>
      {ready.map(p => (
        <div key={p.code} className={`reading-path path-${p.code}`} data-testid={`reading-path-${p.code}`}>
          <div className="reading-path-head">
            <h2>{lang === 'it' ? p.title_it : p.title_en}</h2>
            <p>{lang === 'it' ? p.subtitle_it : p.subtitle_en}</p>
            {(lang === 'it' ? p.intro_it : p.intro_en) && (
              <p className="reading-path-intro" data-testid={`reading-path-intro-${p.code}`}>{lang === 'it' ? p.intro_it : p.intro_en}</p>
            )}
          </div>
          <ol className={`reading-path-steps ${p.steps.length > 6 ? 'many' : ''}`}>
            {p.steps.map(st => (
              <li key={st.id} className="reading-path-step">
                <Link to={`/${lang}/article/${st.id}`} data-testid={`reading-path-step-${p.code}-${st.step}`}>
                  <span className="reading-step-num">{st.step}</span>
                  <span className="reading-step-body">
                    <span className="reading-step-meta">{st.year} · {st.kind === 'case_history' ? 'CASE HISTORY' : st.kind === 'editorial' ? (lang === 'it' ? 'INCHIESTA' : 'FEATURE') : (lang === 'it' ? 'TEORIA' : 'THEORY')}</span>
                    <span className="reading-step-title">{lang === 'it' ? st.title_it : st.title_en}</span>
                    <span className="reading-step-note">{lang === 'it' ? st.note_it : st.note_en}</span>
                  </span>
                  <ChevronRight size={18} className="reading-step-arrow" />
                </Link>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
};

const PathNavigator = ({ lang, articleId }) => {
  const paths = useReadingPaths();
  const hit = paths.map(p => ({ p, i: p.steps.findIndex(s => s.id === articleId) })).find(x => x.i >= 0);
  if (!hit) return null;
  const { p, i } = hit;
  const prev = p.steps[i - 1];
  const next = p.steps[i + 1];
  return (
    <nav className="path-navigator" data-testid="path-navigator" aria-label={lang === 'it' ? 'Percorso di lettura' : 'Reading path'}>
      <div className="path-navigator-head">
        <span className="path-navigator-kicker">{lang === 'it' ? 'Percorso' : 'Reading path'}</span>
        <Link to={`/${lang}/timeline`} className="path-navigator-title">{lang === 'it' ? p.title_it : p.title_en}</Link>
        <span className="path-navigator-progress">{lang === 'it' ? 'Tappa' : 'Step'} {i + 1} / {p.steps.length}</span>
      </div>
      <div className="path-navigator-dots" aria-hidden="true">
        {p.steps.map((s, k) => <Link key={s.id} to={`/${lang}/article/${s.id}`} className={`path-dot ${k === i ? 'current' : k < i ? 'done' : ''}`} title={lang === 'it' ? s.title_it : s.title_en} />)}
      </div>
      {(lang === 'it' ? p.steps[i].bridge_it : p.steps[i].bridge_en) && (
        <p className="path-navigator-bridge" data-testid="path-bridge">
          <strong>{lang === 'it' ? 'Il filo del percorso' : 'The thread'}</strong> — {lang === 'it' ? p.steps[i].bridge_it : p.steps[i].bridge_en}
        </p>
      )}
      <div className="path-navigator-links">
        {prev ? (
          <Link to={`/${lang}/article/${prev.id}`} className="path-link prev" data-testid="path-prev">
            <ChevronLeft size={16} /><span><small>{lang === 'it' ? 'Precedente' : 'Previous'}</small>{lang === 'it' ? prev.title_it : prev.title_en}</span>
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`/${lang}/article/${next.id}`} className="path-link next" data-testid="path-next">
            <span><small>{lang === 'it' ? 'Prossima tappa' : 'Next step'}</small>{lang === 'it' ? next.title_it : next.title_en}</span><ChevronRight size={16} />
          </Link>
        ) : (
          <Link to={`/${lang}/timeline`} className="path-link next" data-testid="path-done">
            <span><small>{lang === 'it' ? 'Percorso completato' : 'Path completed'}</small>{lang === 'it' ? 'Torna alla Timeline' : 'Back to the Timeline'}</span><ChevronRight size={16} />
          </Link>
        )}
      </div>
    </nav>
  );
};

// Header component
const Header = ({ lang, setLang }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const t = translations[lang];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/${lang}/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="header" data-testid="main-header">
      <div className="header-top">
        <div className="header-container">
          <div className="header-date">
            {new Date().toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
          <div className="header-actions">
            <NotificationBell lang={lang} />
            <div className="lang-switch" data-testid="language-switch">
              <button 
                className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => setLang('en')}
                data-testid="lang-en"
              >
                EN
              </button>
              <span className="lang-divider">|</span>
              <button 
                className={`lang-btn ${lang === 'it' ? 'active' : ''}`}
                onClick={() => setLang('it')}
                data-testid="lang-it"
              >
                IT
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="header-main">
        <div className="header-container">
          <Link to={`/${lang}/`} className="logo" data-testid="logo">
            <span className="logo-text">HORIZON</span>
            <span className="logo-brief">BRIEF</span>
          </Link>
          
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <form onSubmit={handleSearch} className={`search-form ${mobileMenuOpen ? 'show' : ''}`}>
            <div className="search-input-wrapper">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                data-testid="search-input"
              />
            </div>
          </form>
        </div>
      </div>
      
      <nav className={`nav-main ${mobileMenuOpen ? 'show' : ''}`}>
        <div className="header-container">
          <Link to={`/${lang}/`} className="nav-link" data-testid="nav-home">{t.home}</Link>
          <Link to={`/${lang}/category/science`} className="nav-link" data-testid="nav-science">Science</Link>
          <Link to={`/${lang}/category/biology`} className="nav-link" data-testid="nav-biology">Biology</Link>
          <Link to={`/${lang}/category/technology`} className="nav-link" data-testid="nav-technology">Technology</Link>
          <Link to={`/${lang}/category/hardware`} className="nav-link" data-testid="nav-hardware">Hardware</Link>
          <Link to={`/${lang}/category/economics`} className="nav-link" data-testid="nav-economics">Economics</Link>
          <Link to={`/${lang}/category/finance`} className="nav-link" data-testid="nav-finance">Finance</Link>
          <Link to={`/${lang}/category/economic_history`} className="nav-link" data-testid="nav-economic-history">Econ. History</Link>
          <Link to={`/${lang}/timeline`} className="nav-link nav-link-highlight" data-testid="nav-timeline">
            <Calendar size={14} style={{ marginRight: 4 }} />
            Timeline
          </Link>
          <Link to={`/${lang}/newsletter`} className="nav-link nav-link-newsletter" data-testid="nav-newsletter">
            <Mail size={14} style={{ marginRight: 4 }} />
            {lang === 'it' ? 'Il Brief delle 7' : '7 O\'Clock Brief'}
          </Link>
        </div>
      </nav>
    </header>
  );
};

// Breaking news ticker
const BreakingTicker = ({ articles, lang }) => {
  const t = translations[lang];
  
  if (!articles.length) return null;
  
  return (
    <div className="breaking-ticker" data-testid="breaking-ticker">
      <span className="breaking-label">{t.breaking}</span>
      <div className="ticker-wrapper">
        <div className="ticker-content">
          {articles.map((article, idx) => (
            <Link 
              key={article.id} 
              to={`/${lang}/article/${article.id}`}
              className="ticker-item"
            >
              {lang === 'it' && article.title_it ? article.title_it : article.title_en}
              {idx < articles.length - 1 && <span className="ticker-separator">•</span>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Article card component
const searchSnippet = (article, lang, q) => {
  if (!q) return null;
  const rx = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
  const fields = lang === 'it' ? ['content_it', 'content_en', 'summary_it'] : ['content_en', 'content_it', 'summary_en'];
  for (const f of fields) {
    const txt = (article[f] || '').replace(/!\[[^\]]*\]\([^)]*\)/g, '').replace(/[#*_>|`]/g, '');
    const m = rx.exec(txt);
    if (m) {
      const a = Math.max(0, m.index - 90), b = Math.min(txt.length, m.index + m[0].length + 110);
      const seg = (a > 0 ? '…' : '') + txt.slice(a, b).replace(/\s+/g, ' ') + (b < txt.length ? '…' : '');
      const parts = seg.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i'));
      return parts.map((pt, i) => rx.test(pt) && pt.toLowerCase() === q.toLowerCase() ? <mark key={i}>{pt}</mark> : pt);
    }
  }
  return null;
};

const ArticleCard = ({ article, lang, featured = false, highlight = '' }) => {
  const t = translations[lang];
  const title = lang === 'it' && article.title_it ? article.title_it : article.title_en;
  const summary = lang === 'it' && article.summary_it ? article.summary_it : article.summary_en;
  const href = `/${lang}/article/${article.id}${highlight ? `?q=${encodeURIComponent(highlight)}` : ''}`;
  const snippet = searchSnippet(article, lang, highlight);
  const CategoryIcon = categoryIcons[article.category] || Beaker;
  const categoryColor = categoryColors[article.category] || "#1a56db";
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <article className={`article-card ${featured ? 'featured' : ''}`} data-testid={`article-card-${article.id}`}>
      {article.image_url && (
        <Link to={href} className={`article-image ${featured ? '' : 'small'}`} tabIndex={-1} aria-hidden="true">
          <img src={article.image_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.closest('.article-image').style.display = 'none'; }} />
          <span className="article-image-credit">{article.source_name}</span>
        </Link>
      )}
      <div className="article-content">
        <div className="article-meta">
          <span 
            className="article-category" 
            style={{ color: categoryColor }}
            data-testid={`category-${article.category}`}
          >
            <CategoryIcon size={14} />
            {article.category.toUpperCase()}
          </span>
          <span className="article-time">
            <Clock size={12} />
            {formatDate(article.published_at)}
          </span>
          {article.version > 1 && (
            <span className="article-version" title={`${t.version} ${article.version}`}>
              <RefreshCw size={12} />
            </span>
          )}
        </div>
        <Link to={href} className="article-title-link">
          <h3 className="article-title">{title}</h3>
        </Link>
        {snippet && <p className="article-snippet" data-testid={`search-snippet-${article.id}`}>{snippet}</p>}
        {!snippet && (featured || summary.length < 200) && (
          <p className="article-summary">{summary.length > (featured ? 300 : 150) ? summary.substring(0, featured ? 300 : 150).trimEnd() + '…' : summary}</p>
        )}
        <div className="article-footer">
          <span className="article-source">{article.author ? `${article.author} · Horizon Brief` : article.source_name}</span>
          <Link to={href} className="read-more">
            {t.readMore} <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
};

// Sidebar component
const Sidebar = ({ categories, lang, stats, showNewsletter = false }) => {
  const t = translations[lang];
  
  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-section">
        <h3 className="sidebar-title">{t.categories}</h3>
        <ul className="category-list">
          {categories.map(cat => {
            const Icon = categoryIcons[cat.key] || Beaker;
            const color = categoryColors[cat.key];
            return (
              <li key={cat.key}>
                <Link 
                  to={`/${lang}/category/${cat.key}`} 
                  className="category-link"
                  data-testid={`sidebar-category-${cat.key}`}
                >
                  <Icon size={16} style={{ color }} />
                  <span>{lang === 'it' ? cat.name_it : cat.name_en}</span>
                  <span className="category-count">{cat.article_count}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      
      {stats && (
        <div className="sidebar-section stats-section">
          <h3 className="sidebar-title">Stats</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-value">{stats.total_articles}</span>
              <span className="stat-label">{t.totalArticles}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.sources_count}</span>
              <span className="stat-label">Sources</span>
            </div>
          </div>
          <div className="scraping-info">
            <RefreshCw size={12} />
            <span>Auto-refresh: 30 min</span>
          </div>
        </div>
      )}
      
      {showNewsletter && categories.length > 0 && (
        <div className="sidebar-section newsletter-section">
          <NewsletterForm lang={lang} source="sidebar" />
        </div>
      )}
    </aside>
  );
};

// Home page
const HomePage = ({ lang }) => {
  const [articles, setArticles] = useState([]);
  const [breaking, setBreaking] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const t = translations[lang];
  useSeo({ path: '/', lang, description: SITE_DESCRIPTION[lang] });

  const fetchData = useCallback(async () => {
    try {
      const [articlesRes, breakingRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/articles?page_size=15`),
        axios.get(`${API}/articles/breaking?limit=5`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`)
      ]);
      
      setArticles(articlesRes.data.articles);
      setBreaking(breakingRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerScrape = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/scrape`);
      toast.info("Fetching latest news...");
      const checkStatus = async () => {
        const res = await axios.get(`${API}/scrape/status`);
        if (!res.data.scraping_in_progress) {
          await fetchData();
          setScraping(false);
          toast.success("News updated!");
        } else {
          setTimeout(checkStatus, 3000);
        }
      };
      setTimeout(checkStatus, 2000);
    } catch (err) {
      console.error("Scrape error:", err);
      setScraping(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div className="loading" data-testid="loading">{t.loading}</div>;
  }

  const featuredArticle = articles[0];
  const regularArticles = articles.slice(1);

  return (
    <div className="home-page" data-testid="home-page">
      <BreakingTicker articles={breaking} lang={lang} />
      
      <div className="main-content">
        <main className="content-area">
          <div className="section-header">
            <h2 className="section-title">{t.latest}</h2>
            <button 
              className="refresh-btn" 
              onClick={triggerScrape} 
              disabled={scraping}
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={scraping ? 'spinning' : ''} />
              {scraping ? t.scraping : t.refresh}
            </button>
          </div>
          
          {featuredArticle && (
            <ArticleCard article={featuredArticle} lang={lang} featured={true} />
          )}

          <DataOfTheDay lang={lang} />
          
          <div className="articles-grid">
            {regularArticles.slice(0, 6).map(article => (
              <ArticleCard key={article.id} article={article} lang={lang} />
            ))}
          </div>

          <VideoWall lang={lang} />

          <div className="articles-grid">
            {regularArticles.slice(6).map(article => (
              <ArticleCard key={article.id} article={article} lang={lang} />
            ))}
          </div>
          
          {articles.length === 0 && (
            <div className="no-results" data-testid="no-results">
              <p>{t.noResults}</p>
              <button onClick={triggerScrape} className="btn-primary" data-testid="fetch-news-btn">
                <RefreshCw size={16} />
                Fetch News
              </button>
            </div>
          )}
        </main>
        
        <Sidebar categories={categories} lang={lang} stats={stats} showNewsletter={true} />
      </div>
    </div>
  );
};

// Category page
const CategoryPage = ({ lang }) => {
  const { categoryId } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const t = translations[lang];

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/articles?category=${categoryId}&page=${page}&page_size=20`),
          axios.get(`${API}/categories`)
        ]);
        setArticles(articlesRes.data.articles);
        setTotalPages(articlesRes.data.total_pages);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Error fetching category articles:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [categoryId, page]);

  const currentCategory = categories.find(c => c.key === categoryId);
  const categoryName = currentCategory 
    ? (lang === 'it' ? currentCategory.name_it : currentCategory.name_en)
    : categoryId;
  useSeo({
    title: categoryName,
    path: `/category/${categoryId}`,
    lang,
    description: lang === 'it'
      ? `Le ultime notizie di ${categoryName} su Horizon Brief, riscritte da fonti primarie: università, istituti di ricerca, banche centrali e aziende tech.`
      : `The latest ${categoryName} news on Horizon Brief, rewritten from primary sources: universities, research institutes, central banks and tech companies.`,
  });

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="category-page" data-testid="category-page">
      <div className="main-content">
        <main className="content-area">
          <div className="section-header">
            <h1 className="page-title" style={{ color: categoryColors[categoryId] }}>
              {categoryName}
            </h1>
          </div>
          
          <div className="articles-list">
            {articles.map(article => (
              <ArticleCard key={article.id} article={article} lang={lang} />
            ))}
          </div>
          
          {articles.length === 0 && (
            <div className="no-results">{t.noResults}</div>
          )}
          
          {totalPages > 1 && (
            <div className="pagination" data-testid="pagination">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">Page {page} of {totalPages}</span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </main>
        
        <Sidebar categories={categories} lang={lang} />
      </div>
    </div>
  );
};

// Article detail page

// Convert LaTeX-native delimiters to markdown-math dollar delimiters
// so remark-math + rehype-katex can render them. Preserve line structure.
const preprocessMath = (raw) => {
  if (!raw) return raw;
  let s = raw;
  // Display math: \[ ... \]  ->  $$ ... $$
  s = s.replace(/\\\[([\s\S]*?)\\\]/g, (_, expr) => `\n\n$$\n${expr.trim()}\n$$\n\n`);
  // Inline math: \( ... \)  ->  $ ... $
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, (_, expr) => `$${expr.trim()}$`);
  return s;
};

const AVAILABLE_LANGS = [
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'fr', label: 'FR', name: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'PT', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', label: 'ZH', name: '简体中文', flag: '🇨🇳' },
  { code: 'ar', label: 'AR', name: 'العربية', flag: '🇸🇦' },
];
const RTL_LANGS = ['ar'];
const READ_IN_LABEL = {
  en: 'Read in', it: 'Leggi in', fr: 'Lire en', de: 'Lesen in',
  es: 'Leer en', pt: 'Ler em', zh: '阅读语言', ar: 'اقرأ باللغة',
};
const TRANSLATING_LABEL = {
  en: 'Translating the article with AI… this may take 20–40 seconds the first time.',
  it: 'Traduzione dell\'articolo con l\'AI in corso… al primo avvio può richiedere 20–40 secondi.',
  fr: 'Traduction de l\'article par IA… cela peut prendre 20 à 40 secondes la première fois.',
  de: 'Der Artikel wird per KI übersetzt… beim ersten Mal kann dies 20–40 Sekunden dauern.',
  es: 'Traduciendo el artículo con IA… la primera vez puede tardar 20–40 segundos.',
  pt: 'A traduzir o artigo com IA… da primeira vez pode demorar 20–40 segundos.',
  zh: '正在使用 AI 翻译文章… 首次翻译可能需要 20–40 秒。',
  ar: 'جارٍ ترجمة المقال بالذكاء الاصطناعي… قد يستغرق ذلك 20–40 ثانية في المرة الأولى.',
};
const TRANSLATE_ERROR_LABEL = {
  en: 'Translation failed. Please try again.', it: 'Traduzione non riuscita. Riprova.',
  fr: 'Échec de la traduction. Réessayez.', de: 'Übersetzung fehlgeschlagen. Bitte erneut versuchen.',
  es: 'La traducción falló. Inténtalo de nuevo.', pt: 'A tradução falhou. Tente novamente.',
  zh: '翻译失败，请重试。', ar: 'فشلت الترجمة. يرجى المحاولة مرة أخرى.',
};

const ArticlePage = ({ lang }) => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedTheories, setRelatedTheories] = useState([]);
  const [readLang, setReadLang] = useState(lang);
  const [translated, setTranslated] = useState(null); // {title, summary, content}
  const [translating, setTranslating] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const highlightTerm = (searchParams.get('q') || '').trim();
  const articleRef = useRef(null);
  const t = translations[lang];
  const navigate = useNavigate();
  const seoTitle = article ? (lang === 'it' && article.title_it ? article.title_it : article.title_en) : undefined;
  const seoDesc = article ? (lang === 'it' && article.summary_it ? article.summary_it : article.summary_en) : undefined;
  const seoJsonLd = useMemo(() => (article ? articleJsonLd(article, lang) : undefined), [article, lang]);
  useSeo({ title: seoTitle, description: seoDesc, path: `/article/${articleId}`, image: article?.image_url, type: 'article', lang, jsonLd: seoJsonLd });

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API}/articles/${articleId}`);
        setArticle(res.data);
        // Fetch related theories only for economic_history non-theory articles
        const isTheory = (res.data.tags || []).includes('curated_theory');
        if (res.data.category === 'economic_history' && !isTheory) {
          try {
            const rel = await axios.get(`${API}/articles/${articleId}/related-theories`);
            setRelatedTheories(rel.data || []);
          } catch (_) {
            setRelatedTheories([]);
          }
        }
      } catch (err) {
        console.error("Error fetching article:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    setReadLang(lang);
    setTranslated(null);
  }, [articleId, lang]);

  // When user changes the reading language, fetch (or generate) translation
  useEffect(() => {
    if (!article) return;
    if (readLang === 'en' || readLang === 'it') {
      setTranslated(null);
      return;
    }
    // Try cached first, then generate
    const cached = article.translations && article.translations[readLang];
    if (cached && cached.title && cached.content) {
      setTranslated({ title: cached.title, summary: cached.summary, content: cached.content });
      setTranslating(false);
      return;
    }
    setTranslating(true);
    const requested = readLang;
    let cancelled = false;
    const url = `${API}/articles/${articleId}/translation/${requested}`;
    const poll = async () => {
      for (let attempt = 0; attempt < 100; attempt++) {
        const res = await axios.get(url, { timeout: 30000, validateStatus: s => s === 200 || s === 202 });
        if (cancelled) return;
        if (res.status === 200) {
          setTranslated({ title: res.data.title, summary: res.data.summary, content: res.data.content });
          setArticle(a => a ? { ...a, translations: { ...(a.translations || {}), [requested]: res.data } } : a);
          return;
        }
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) return;
      }
      throw new Error('Translation timed out');
    };
    poll()
      .catch(err => {
        if (cancelled) return;
        console.error('Translation error', err);
        toast.error(TRANSLATE_ERROR_LABEL[lang] || TRANSLATE_ERROR_LABEL.en);
        setReadLang(lang);
      })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [readLang, article, articleId, lang]);

  useEffect(() => {
    if (!highlightTerm || !article || loading || translating || !articleRef.current) return;
    const root = articleRef.current;
    root.querySelectorAll('mark.search-hit').forEach(m => { const p = m.parentNode; p.replaceChild(document.createTextNode(m.textContent), m); p.normalize(); });
    const rx = new RegExp(highlightTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: n => (n.parentElement.closest('script,style,svg,button') ? NodeFilter.FILTER_REJECT : rx.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP) });
    const timer = setTimeout(() => {
      const node = walker.nextNode();
      if (!node) return;
      const m = rx.exec(node.nodeValue);
      const hit = node.splitText(m.index); hit.splitText(m[0].length);
      const mark = document.createElement('mark'); mark.className = 'search-hit'; mark.setAttribute('data-testid', 'search-hit');
      hit.parentNode.replaceChild(mark, hit); mark.appendChild(hit);
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(timer);
  }, [highlightTerm, article, loading, translating, readLang, translated]);

  const downloadPdf = async () => {
    if (!articleRef.current || pdfBusy) return;
    setPdfBusy(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = articleRef.current;
      el.classList.add('pdf-export');
      const shownTitle = el.querySelector('.article-title-full')?.textContent || seoTitle || 'horizon-brief';
      const slug = shownTitle.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').slice(0, 80) || 'horizon-brief';
      const name = `${slug}-${readLang}`;
      await html2pdf().set({
        margin: [12, 12, 14, 12], filename: `${name}.pdf`, image: { type: 'jpeg', quality: 0.85 },
        html2canvas: { scale: 1.6, useCORS: true, logging: false, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, pagebreak: { mode: ['css', 'legacy'], avoid: ['figure', 'table', 'tr', '.wpc', 'h2', 'h3'] },
      }).from(el).save();
    } catch (e) {
      console.error('PDF error', e);
      toast.error(lang === 'it' ? 'Impossibile generare il PDF' : 'Could not generate the PDF');
    } finally {
      articleRef.current?.classList.remove('pdf-export');
      setPdfBusy(false);
    }
  };

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  if (!article) {
    return (
      <div className="not-found" data-testid="article-not-found">
        <p>Article not found</p>
        <button onClick={() => navigate(`/${lang}/`)} className="btn-primary">{t.backToHome}</button>
      </div>
    );
  }

  const nativeTitle = lang === 'it' && article.title_it ? article.title_it : article.title_en;
  const nativeSummary = lang === 'it' && article.summary_it ? article.summary_it : article.summary_en;
  const nativeContent = lang === 'it' && article.content_it ? article.content_it : article.content_en;
  const isTranslatedLang = readLang !== 'en' && readLang !== 'it';
  const title = isTranslatedLang && translated ? translated.title : (readLang === 'en' ? article.title_en : (readLang === 'it' ? (article.title_it || article.title_en) : nativeTitle));
  const summary = isTranslatedLang && translated ? translated.summary : (readLang === 'en' ? article.summary_en : (readLang === 'it' ? (article.summary_it || article.summary_en) : nativeSummary));
  const content = isTranslatedLang && translated ? translated.content : (readLang === 'en' ? article.content_en : (readLang === 'it' ? (article.content_it || article.content_en) : nativeContent));
  const isRTL = RTL_LANGS.includes(readLang);
  const CategoryIcon = categoryIcons[article.category] || Beaker;
  const categoryColor = categoryColors[article.category];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="article-page" data-testid="article-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <article className="article-full" ref={articleRef}>
        <div className="article-header">
          <div className="article-meta-full">
            <Link 
              to={`/${lang}/category/${article.category}`}
              className="article-category-link"
              style={{ color: categoryColor }}
            >
              <CategoryIcon size={16} />
              {article.category.toUpperCase()}
            </Link>
            <span className="article-date">{formatDate(article.published_at)}</span>
          </div>

          <div className="read-in-selector" data-testid="read-in-selector">
            <span className="read-in-label">
              <Globe size={14} /> {READ_IN_LABEL[readLang] || 'Read in'}:
            </span>
            <div className="read-in-langs">
              {AVAILABLE_LANGS.map(l => (
                <button
                  key={l.code}
                  type="button"
                  disabled={translating}
                  onClick={() => setReadLang(l.code)}
                  className={`read-in-lang ${readLang === l.code ? 'active' : ''}`}
                  aria-pressed={readLang === l.code}
                  data-testid={`read-in-${l.code}`}
                  title={l.name}
                >
                  <span className="read-in-flag" aria-hidden="true">{l.flag}</span>
                  <span className="read-in-code">{l.label}</span>
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="pdf-download-btn" onClick={downloadPdf} disabled={pdfBusy || translating} data-testid="download-pdf-button" data-html2canvas-ignore="true">
            <FileDown size={15} /> {pdfBusy ? (lang === 'it' ? 'Generazione PDF…' : 'Generating PDF…') : (lang === 'it' ? 'Scarica PDF' : 'Download PDF')}
          </button>
          {translating && (
            <div className="translating-banner" role="status" data-testid="translating-indicator">
              <RefreshCw size={18} className="spinning" />
              <span>{TRANSLATING_LABEL[lang] || TRANSLATING_LABEL.en}</span>
            </div>
          )}

          <h1 className="article-title-full">{title}</h1>
          {(article.author || (article.tags || []).includes('editorial')) && (
            <div className="article-byline" data-testid="article-byline">
              <span className="article-byline-di">{lang === 'it' ? 'di' : 'by'}</span> {article.author || article.source_name.split(' · ')[0]}
              <span className="article-byline-org"> · Horizon Brief</span>
            </div>
          )}
          
          {article.version > 1 && (
            <div className="article-updated">
              <RefreshCw size={14} />
              {t.updated}: {formatDate(article.updated_at)} ({t.version} {article.version})
            </div>
          )}
        </div>
        
        {article.image_url && (
          <div className="article-image-full">
            <img src={article.image_url} alt="" />
          </div>
        )}
        
        <div className={`article-body ${translating ? 'is-translating' : ''}`}>
          <p className="article-lead">{summary}</p>
          {isChipFeature(article.tags) && <SupplyChainMap lang={lang} />}
          {content && (
            <div className="article-content-text">
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  p: ({ node, children }) => (
                    node?.children?.length === 1 && node.children[0].tagName === 'img' ? <>{children}</> : <p>{children}</p>
                  ),
                  img: ({ src, alt }) => { const mk = ((src || '').match(/charts\/(merton-[a-z]+)/) || [])[1]; if (mk && MERTON_CHARTS[mk]) { const MC = MERTON_CHARTS[mk]; return <MC lang={lang} />; } const dk = ((src || '').match(/charts\/(divergence-[a-z]+)/) || [])[1]; if (dk && DIVERGENCE_CHARTS[dk]) { const DC = DIVERGENCE_CHARTS[dk]; return <DC lang={lang} />; } const rk = ((src || '').match(/charts\/(rustbelt-[a-z]+)/) || [])[1]; if (rk && RUSTBELT_CHARTS[rk]) { const RC = RUSTBELT_CHARTS[rk]; return <RC lang={lang} />; } const ek = ((src || '').match(/charts\/(econometrics-[a-z]+)/) || [])[1]; if (ek && ECONOMETRICS_CHARTS[ek]) { const EC = ECONOMETRICS_CHARTS[ek]; return <EC lang={lang} />; } return (src || '').includes('grafico-3-quota-del-lavoro') ? <LabourShareChart lang={lang} /> : (src || '').includes('grafico-1-salari-e-produttivita') ? <WageProductivityChart lang={lang} /> : (src || '').includes('grafico-2-stati-uniti-quote-del-reddito') ? <IncomeSharesChart lang={lang} /> : (src || '').includes('charts/income-deciles') ? <IncomeDecilesChart lang={lang} /> : (src || '').includes('grafico-4-manifattura-sul-pil') ? <ManufacturingShareChart lang={lang} /> : (
                    <figure className="article-figure" data-testid="article-figure">
                      <img src={src} alt={alt || ''} loading="lazy" />
                      {alt && <figcaption>{alt}</figcaption>}
                    </figure>
                  ); },
                }}
              >
                {preprocessMath(content)}
              </ReactMarkdown>
            </div>
          )}
        </div>
        
        <div className="article-source-box">
          <span>{t.source}:</span>
          <a href={article.original_url} target="_blank" rel="noopener noreferrer">
            {article.source_name} <ExternalLink size={14} />
          </a>
        </div>
        
        {article.tags && article.tags.length > 0 && (
          <div className="article-tags">
            {article.tags.map((tag, idx) => (
              <span key={idx} className="tag">{tag}</span>
            ))}
          </div>
        )}

        <PathNavigator lang={lang} articleId={articleId} />

        <aside className="article-newsletter" data-testid="article-newsletter">
          <div className="article-newsletter-text">
            <Mail size={18} />
            <strong>{(NL[lang] || NL.it).articleCta}</strong>
          </div>
          <NewsletterForm lang={lang} compact source="article" />
        </aside>

        {relatedTheories.length > 0 && (
          <section className="related-theories" data-testid="related-theories">
            <div className="related-theories-header">
              <ScrollText size={20} />
              <div>
                <h3>{t.relatedTheories}</h3>
                <span className="related-theories-desc">{t.relatedTheoriesDesc}</span>
              </div>
            </div>
            <div className="related-theories-grid">
              {relatedTheories.map((rt) => {
                const rtTitle = lang === 'it' && rt.title_it ? rt.title_it : rt.title_en;
                const rtSummary = lang === 'it' && rt.summary_it ? rt.summary_it : rt.summary_en;
                return (
                  <Link
                    key={rt.id}
                    to={`/${lang}/article/${rt.id}`}
                    className="related-theory-card"
                    data-testid={`related-theory-${rt.id}`}
                  >
                    <div className="related-theory-year">{rt.historical_year || '?'}</div>
                    <div className="related-theory-body">
                      <span className="related-theory-badge">
                        {lang === 'it' ? 'TEORIA' : 'THEORY'}
                      </span>
                      <h4>{rtTitle}</h4>
                      {rt.theory_author && (
                        <div className="related-theory-author">{rt.theory_author}</div>
                      )}
                      {rtSummary && <p>{rtSummary}...</p>}
                      <span className="related-theory-cta">
                        {t.readMore} <ChevronRight size={14} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </article>
      
      <div className="article-nav">
        <button onClick={() => navigate(-1)} className="btn-secondary">
          ← {t.backToHome}
        </button>
      </div>
    </div>
  );
};

// Search page
const SearchPage = ({ lang }) => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const t = translations[lang];
  useSeo({ title: `${t.searchResults}: ${query}`, path: `/search?q=${encodeURIComponent(query)}`, lang });

  useEffect(() => {
    const searchArticles = async () => {
      if (!query) return;
      setLoading(true);
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/articles?search=${encodeURIComponent(query)}&page_size=50`),
          axios.get(`${API}/categories`)
        ]);
        setArticles(articlesRes.data.articles);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoading(false);
      }
    };
    searchArticles();
  }, [query]);

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="search-page" data-testid="search-page">
      <div className="main-content">
        <main className="content-area">
          <div className="section-header">
            <h1 className="page-title">{t.searchResults}</h1>
            <p className="search-query">{t.resultsFor}: "{query}" ({articles.length})</p>
          </div>
          
          <div className="articles-list">
            {articles.map(article => (
              <ArticleCard key={article.id} article={article} lang={lang} highlight={query} />
            ))}
          </div>
          
          {articles.length === 0 && (
            <div className="no-results">{t.noResults}</div>
          )}
        </main>
        
        <Sidebar categories={categories} lang={lang} />
      </div>
    </div>
  );
};

// Economic Theory Chart Component - renders SVG diagrams for different theory types
const TheoryChart = ({ chartType, color = "#003366" }) => {
  const charts = {
    concept_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <circle cx="150" cy="100" r="60" fill="none" stroke={color} strokeWidth="2"/>
        <text x="150" y="95" textAnchor="middle" fill={color} fontSize="14" fontWeight="600">Invisible</text>
        <text x="150" y="115" textAnchor="middle" fill={color} fontSize="14" fontWeight="600">Hand</text>
        <path d="M 90 100 Q 60 60 30 80" stroke={color} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)"/>
        <path d="M 210 100 Q 240 60 270 80" stroke={color} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)"/>
        <path d="M 90 100 Q 60 140 30 130" stroke={color} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)"/>
        <path d="M 210 100 Q 240 140 270 130" stroke={color} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)"/>
        <text x="30" y="75" fill={color} fontSize="11">Self-interest</text>
        <text x="230" y="75" fill={color} fontSize="11">Free markets</text>
        <text x="30" y="150" fill={color} fontSize="11">Division of labor</text>
        <text x="230" y="150" fill={color} fontSize="11">Public good</text>
      </svg>
    ),
    supply_demand_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Q</text>
        <text x="30" y="20" fontSize="11">P</text>
        <line x1="40" y1="30" x2="270" y2="170" stroke="#dc2626" strokeWidth="2.5"/>
        <line x1="40" y1="170" x2="270" y2="30" stroke="#1a56db" strokeWidth="2.5"/>
        <circle cx="155" cy="100" r="5" fill={color}/>
        <text x="165" y="105" fontSize="11" fill={color}>Equilibrium</text>
        <text x="220" y="45" fontSize="11" fill="#1a56db">Supply</text>
        <text x="220" y="165" fontSize="11" fill="#dc2626">Demand</text>
      </svg>
    ),
    trade_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="40" y="60" width="80" height="80" fill="none" stroke={color} strokeWidth="2"/>
        <rect x="180" y="60" width="80" height="80" fill="none" stroke={color} strokeWidth="2"/>
        <text x="80" y="55" textAnchor="middle" fill={color} fontSize="12" fontWeight="600">Country A</text>
        <text x="220" y="55" textAnchor="middle" fill={color} fontSize="12" fontWeight="600">Country B</text>
        <text x="80" y="105" textAnchor="middle" fontSize="11">Wine</text>
        <text x="80" y="120" textAnchor="middle" fontSize="10" fill="#666">80 hrs</text>
        <text x="220" y="105" textAnchor="middle" fontSize="11">Cloth</text>
        <text x="220" y="120" textAnchor="middle" fontSize="10" fill="#666">100 hrs</text>
        <path d="M 120 100 Q 150 80 180 100" stroke={color} strokeWidth="2" fill="none" markerEnd="url(#arrow)"/>
        <path d="M 180 120 Q 150 140 120 120" stroke={color} strokeWidth="2" fill="none" markerEnd="url(#arrow)"/>
        <text x="150" y="75" textAnchor="middle" fontSize="10" fill={color}>Trade</text>
      </svg>
    ),
    population_food_curves: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Time</text>
        <path d="M 40 170 Q 100 160 160 100 Q 200 60 260 30" stroke="#dc2626" strokeWidth="2.5" fill="none"/>
        <path d="M 40 170 L 260 100" stroke="#047857" strokeWidth="2.5" fill="none"/>
        <text x="200" y="55" fontSize="11" fill="#dc2626">Population (geometric)</text>
        <text x="200" y="120" fontSize="11" fill="#047857">Food (arithmetic)</text>
      </svg>
    ),
    marginal_utility_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Quantity</text>
        <text x="30" y="20" fontSize="11">MU</text>
        <path d="M 40 40 Q 120 60 200 130 Q 240 165 280 175" stroke={color} strokeWidth="2.5" fill="none"/>
        <text x="180" y="80" fontSize="11" fill={color}>Diminishing</text>
        <text x="180" y="95" fontSize="11" fill={color}>Marginal Utility</text>
      </svg>
    ),
    equilibrium_equations: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="40" width="240" height="120" rx="4" fill="none" stroke={color} strokeWidth="2"/>
        <text x="150" y="70" textAnchor="middle" fill={color} fontSize="13" fontFamily="monospace">Σ Dᵢ(p) = Σ Sᵢ(p)</text>
        <text x="150" y="95" textAnchor="middle" fill="#666" fontSize="11">∀ i ∈ markets</text>
        <text x="150" y="130" textAnchor="middle" fontSize="11" fill={color}>General Equilibrium</text>
        <text x="150" y="147" textAnchor="middle" fontSize="10" fill="#666">All markets clear simultaneously</text>
      </svg>
    ),
    marshall_scissors: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Q</text>
        <text x="30" y="20" fontSize="11">P</text>
        <line x1="60" y1="160" x2="260" y2="40" stroke="#1a56db" strokeWidth="3"/>
        <line x1="60" y1="40" x2="260" y2="160" stroke="#dc2626" strokeWidth="3"/>
        <circle cx="160" cy="100" r="6" fill={color} stroke="white" strokeWidth="2"/>
        <text x="170" y="105" fontSize="11" fill={color} fontWeight="600">P*, Q*</text>
      </svg>
    ),
    pareto_frontier: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Person A</text>
        <text x="30" y="20" fontSize="10">Person B</text>
        <path d="M 260 40 Q 200 50 140 80 Q 80 130 50 170" stroke={color} strokeWidth="2.5" fill="none"/>
        <circle cx="100" cy="130" r="4" fill="#dc2626"/>
        <text x="110" y="135" fontSize="10">Inefficient</text>
        <text x="130" y="60" fontSize="11" fill={color}>Pareto Frontier</text>
      </svg>
    ),
    is_lm_model: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Y</text>
        <text x="20" y="20" fontSize="11">r</text>
        <line x1="70" y1="40" x2="270" y2="160" stroke="#dc2626" strokeWidth="2.5"/>
        <line x1="70" y1="160" x2="270" y2="40" stroke="#1a56db" strokeWidth="2.5"/>
        <text x="230" y="55" fontSize="11" fill="#1a56db">LM</text>
        <text x="230" y="155" fontSize="11" fill="#dc2626">IS</text>
        <circle cx="170" cy="100" r="5" fill={color}/>
        <text x="180" y="105" fontSize="10">Equilibrium</text>
      </svg>
    ),
    is_lm_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <line x1="70" y1="40" x2="270" y2="150" stroke="#dc2626" strokeWidth="2.5"/>
        <line x1="70" y1="150" x2="270" y2="40" stroke="#1a56db" strokeWidth="2.5"/>
        <line x1="90" y1="70" x2="250" y2="180" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="4"/>
        <text x="230" y="45" fontSize="11" fill="#1a56db">LM</text>
        <text x="230" y="145" fontSize="11" fill="#dc2626">IS'</text>
        <path d="M 155 100 L 175 115" stroke={color} strokeWidth="1" markerEnd="url(#arrow)"/>
      </svg>
    ),
    regression_plot: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        {[[60,150],[80,140],[100,125],[120,135],[140,110],[160,95],[180,85],[200,80],[220,65],[240,55],[260,40]].map((p,i)=>(<circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#dc2626"/>))}
        <line x1="50" y1="155" x2="265" y2="45" stroke={color} strokeWidth="2"/>
        <text x="185" y="70" fontSize="11" fill={color} fontFamily="monospace">y = α + βx + ε</text>
      </svg>
    ),
    payoff_matrix: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="80" y="40" width="80" height="60" fill="none" stroke={color} strokeWidth="1.5"/>
        <rect x="160" y="40" width="80" height="60" fill="none" stroke={color} strokeWidth="1.5"/>
        <rect x="80" y="100" width="80" height="60" fill="none" stroke={color} strokeWidth="1.5"/>
        <rect x="160" y="100" width="80" height="60" fill="none" stroke={color} strokeWidth="1.5"/>
        <text x="120" y="75" textAnchor="middle" fontSize="14" fontWeight="600">(3,3)</text>
        <text x="200" y="75" textAnchor="middle" fontSize="14" fontWeight="600">(0,5)</text>
        <text x="120" y="135" textAnchor="middle" fontSize="14" fontWeight="600">(5,0)</text>
        <text x="200" y="135" textAnchor="middle" fontSize="14" fontWeight="600">(1,1)</text>
        <text x="120" y="30" textAnchor="middle" fontSize="10">Cooperate</text>
        <text x="200" y="30" textAnchor="middle" fontSize="10">Defect</text>
        <text x="60" y="75" textAnchor="middle" fontSize="10">Coop</text>
        <text x="60" y="135" textAnchor="middle" fontSize="10">Defect</text>
      </svg>
    ),
    indifference_curves: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <path d="M 50 160 Q 130 130 200 100 Q 250 80 280 70" stroke={color} strokeWidth="2" fill="none"/>
        <path d="M 50 130 Q 130 90 200 60 Q 250 45 280 40" stroke={color} strokeWidth="2" fill="none"/>
        <path d="M 50 100 Q 130 60 200 30 Q 250 20 280 15" stroke={color} strokeWidth="2" fill="none"/>
        <text x="230" y="70" fontSize="10" fill={color}>U₁</text>
        <text x="230" y="40" fontSize="10" fill={color}>U₂</text>
        <text x="230" y="15" fontSize="10" fill={color}>U₃</text>
      </svg>
    ),
    state_space_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <circle cx="80" cy="100" r="30" fill="none" stroke={color} strokeWidth="2"/>
        <circle cx="220" cy="100" r="30" fill="none" stroke={color} strokeWidth="2"/>
        <text x="80" y="105" textAnchor="middle" fontSize="12" fontWeight="600">Supply</text>
        <text x="220" y="105" textAnchor="middle" fontSize="12" fontWeight="600">Demand</text>
        <line x1="110" y1="100" x2="190" y2="100" stroke={color} strokeWidth="2" markerEnd="url(#arrow)"/>
        <line x1="190" y1="100" x2="110" y2="100" stroke={color} strokeWidth="2" markerEnd="url(#arrow)"/>
        <text x="150" y="85" textAnchor="middle" fontSize="11" fill={color}>∃ p* : S(p*) = D(p*)</text>
        <text x="150" y="155" textAnchor="middle" fontSize="10" fill="#666">Existence Theorem</text>
      </svg>
    ),
    quantity_theory_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="70" width="240" height="60" rx="4" fill="none" stroke={color} strokeWidth="2"/>
        <text x="150" y="105" textAnchor="middle" fill={color} fontSize="20" fontFamily="monospace" fontWeight="600">M · V = P · Y</text>
        <text x="60" y="150" fontSize="10">Money</text>
        <text x="115" y="150" fontSize="10">Velocity</text>
        <text x="180" y="150" fontSize="10">Prices</text>
        <text x="235" y="150" fontSize="10">Output</text>
      </svg>
    ),
    phillips_curve_shifts: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Unemp.</text>
        <text x="20" y="20" fontSize="11">Inflation</text>
        <path d="M 60 40 Q 100 100 260 170" stroke="#1a56db" strokeWidth="2" fill="none"/>
        <path d="M 60 80 Q 100 130 260 175" stroke="#dc2626" strokeWidth="2" strokeDasharray="4" fill="none"/>
        <line x1="150" y1="30" x2="150" y2="180" stroke={color} strokeWidth="2"/>
        <text x="160" y="45" fontSize="10" fill={color}>NAIRU</text>
        <text x="200" y="120" fontSize="10" fill="#1a56db">Short-run</text>
      </svg>
    ),
    rbc_impulse_response: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">t</text>
        <text x="20" y="20" fontSize="11">GDP</text>
        <line x1="40" y1="120" x2="280" y2="120" stroke="#999" strokeWidth="1" strokeDasharray="3"/>
        <path d="M 40 120 L 80 40 Q 120 60 160 90 Q 200 110 240 118 L 280 120" stroke={color} strokeWidth="2.5" fill="none"/>
        <text x="90" y="30" fontSize="10" fill={color}>Tech Shock</text>
      </svg>
    ),
    phillips_curve_nk: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="60" width="240" height="80" rx="4" fill="none" stroke={color} strokeWidth="2"/>
        <text x="150" y="95" textAnchor="middle" fill={color} fontSize="14" fontFamily="monospace">π = βE[π₊₁] + κ·y</text>
        <text x="150" y="120" textAnchor="middle" fontSize="10" fill="#666">New Keynesian Phillips Curve</text>
      </svg>
    ),
    prospect_theory_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="100" x2="280" y2="100" stroke="#333" strokeWidth="2"/>
        <line x1="160" y1="180" x2="160" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="105" fontSize="10">Outcome</text>
        <text x="140" y="20" fontSize="10">Value</text>
        <path d="M 60 170 Q 100 150 140 120 Q 155 110 160 100" stroke="#dc2626" strokeWidth="2.5" fill="none"/>
        <path d="M 160 100 Q 180 90 220 65 Q 260 50 275 45" stroke="#047857" strokeWidth="2.5" fill="none"/>
        <text x="70" y="150" fontSize="10" fill="#dc2626">Losses</text>
        <text x="210" y="65" fontSize="10" fill="#047857">Gains</text>
      </svg>
    ),
    regression_discontinuity: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <line x1="160" y1="180" x2="160" y2="20" stroke="#999" strokeWidth="1.5" strokeDasharray="4"/>
        <line x1="50" y1="150" x2="155" y2="105" stroke="#1a56db" strokeWidth="2.5"/>
        <line x1="165" y1="70" x2="270" y2="45" stroke="#dc2626" strokeWidth="2.5"/>
        <text x="165" y="30" fontSize="10">Cutoff</text>
        <text x="60" y="145" fontSize="10" fill="#1a56db">Control</text>
        <text x="200" y="60" fontSize="10" fill="#dc2626">Treated</text>
      </svg>
    ),
    growth_rate_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Time</text>
        <text x="20" y="20" fontSize="11">GDP</text>
        <path d="M 40 170 Q 100 155 160 125 Q 200 100 240 60 L 280 30" stroke={color} strokeWidth="2.5" fill="none"/>
        <text x="180" y="85" fontSize="11" fill={color}>g = f(R&D, H)</text>
      </svg>
    ),
    wealth_inequality_curve: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">Time</text>
        <path d="M 40 80 L 100 100 L 160 120 L 220 90 L 280 40" stroke="#dc2626" strokeWidth="2.5" fill="none"/>
        <text x="150" y="60" fontSize="11" fill="#dc2626" fontFamily="monospace">r &gt; g</text>
        <text x="120" y="145" fontSize="10">Top 10% wealth share</text>
      </svg>
    ),
    dsge_impulse_response: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="100" x2="280" y2="100" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <path d="M 40 100 L 80 40 Q 130 60 180 100 Q 220 130 260 100" stroke="#1a56db" strokeWidth="2" fill="none"/>
        <path d="M 40 100 L 90 130 Q 150 110 200 100" stroke="#dc2626" strokeWidth="2" fill="none"/>
        <text x="100" y="30" fontSize="10" fill="#1a56db">Output</text>
        <text x="120" y="130" fontSize="10" fill="#dc2626">Inflation</text>
      </svg>
    ),
    network_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        {[[80,60],[220,60],[150,110],[70,160],[230,160]].map((p,i)=>(
          <circle key={i} cx={p[0]} cy={p[1]} r="10" fill={color} opacity="0.7"/>
        ))}
        <line x1="80" y1="60" x2="220" y2="60" stroke={color} strokeWidth="1.5"/>
        <line x1="80" y1="60" x2="150" y2="110" stroke={color} strokeWidth="1.5"/>
        <line x1="220" y1="60" x2="150" y2="110" stroke={color} strokeWidth="1.5"/>
        <line x1="150" y1="110" x2="70" y2="160" stroke={color} strokeWidth="1.5"/>
        <line x1="150" y1="110" x2="230" y2="160" stroke={color} strokeWidth="1.5"/>
        <line x1="70" y1="160" x2="230" y2="160" stroke={color} strokeWidth="1.5"/>
        <text x="150" y="40" textAnchor="middle" fontSize="11" fill={color}>Agent-Based Model</text>
      </svg>
    ),
    capital_labor_diagram: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="60" width="100" height="90" fill="none" stroke="#dc2626" strokeWidth="2"/>
        <rect x="170" y="60" width="100" height="90" fill="none" stroke="#1a56db" strokeWidth="2"/>
        <text x="80" y="55" textAnchor="middle" fill="#dc2626" fontSize="12" fontWeight="600">Capital</text>
        <text x="220" y="55" textAnchor="middle" fill="#1a56db" fontSize="12" fontWeight="600">Labor</text>
        <text x="80" y="110" textAnchor="middle" fontSize="11">Surplus</text>
        <text x="220" y="110" textAnchor="middle" fontSize="11">Value</text>
        <path d="M 220 130 Q 150 160 80 130" stroke={color} strokeWidth="2" fill="none" markerEnd="url(#arrow)"/>
        <text x="150" y="180" textAnchor="middle" fontSize="10" fill={color}>Surplus extraction</text>
      </svg>
    ),
    cobb_douglas_isoquants: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">L</text>
        <text x="30" y="20" fontSize="11">K</text>
        <path d="M 55 175 Q 70 70 170 55" stroke={color} strokeWidth="2" fill="none"/>
        <path d="M 80 178 Q 100 95 230 75" stroke={color} strokeWidth="2" fill="none"/>
        <path d="M 115 179 Q 140 118 275 100" stroke={color} strokeWidth="2" fill="none"/>
        <text x="175" y="50" fontSize="10" fill={color}>Y₁</text>
        <text x="235" y="72" fontSize="10" fill={color}>Y₂</text>
        <text x="265" y="115" fontSize="10" fill={color}>Y₃</text>
        <text x="60" y="35" fontSize="12" fill={color} fontFamily="monospace" fontWeight="600">Y = A·Kᵅ·L¹⁻ᵅ</text>
        <text x="150" y="195" fontSize="9" fill="#666">Isoquants, σ = 1</text>
      </svg>
    ),
    input_output_matrix: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        {[0,1,2,3,4].map(i => [0,1,2,3,4].map(j => (
          <rect key={`${i}-${j}`} x={60 + j * 30} y={40 + i * 24} width="28" height="22" fill={color} opacity={0.15 + (((i * 7 + j * 11) % 9) / 9) * 0.75} />
        )))}
        {['Agr','Ind','Ene','Srv','Fin'].map((t, i) => (<text key={`r${i}`} x="56" y={55 + i * 24} fontSize="9" textAnchor="end" fill="#333">{t}</text>))}
        {['Agr','Ind','Ene','Srv','Fin'].map((t, j) => (<text key={`c${j}`} x={74 + j * 30} y="34" fontSize="9" textAnchor="middle" fill="#333">{t}</text>))}
        <text x="230" y="80" fontSize="12" fill={color} fontFamily="monospace" fontWeight="600">X = AX + Y</text>
        <text x="230" y="100" fontSize="12" fill={color} fontFamily="monospace">X = (I−A)⁻¹Y</text>
        <text x="150" y="185" fontSize="9" fill="#666" textAnchor="middle">Input-output table: who buys from whom</text>
      </svg>
    ),
    reaction_functions: (
      <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
        <line x1="40" y1="180" x2="280" y2="180" stroke="#333" strokeWidth="2"/>
        <line x1="40" y1="180" x2="40" y2="20" stroke="#333" strokeWidth="2"/>
        <text x="285" y="185" fontSize="11">q₁</text>
        <text x="30" y="20" fontSize="11">q₂</text>
        <line x1="50" y1="150" x2="240" y2="60" stroke="#dc2626" strokeWidth="2"/>
        <line x1="80" y1="30" x2="230" y2="170" stroke="#1a56db" strokeWidth="2"/>
        <circle cx="140" cy="100" r="5" fill={color}/>
        <text x="150" y="105" fontSize="11" fill={color}>Cournot Eq.</text>
        <text x="210" y="60" fontSize="10" fill="#dc2626">R₁(q₂)</text>
        <text x="90" y="45" fontSize="10" fill="#1a56db">R₂(q₁)</text>
      </svg>
    ),
  };
  
  const chart = charts[chartType] || charts.concept_diagram;
  
  return (
    <div className="theory-chart">
      <svg width="0" height="0">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color}/>
          </marker>
        </defs>
      </svg>
      {chart}
    </div>
  );
};

// Timeline page - Interactive economic history timeline
const TimelinePage = ({ lang }) => {
  const [periods, setPeriods] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = translations[lang];
  useSeo({
    title: t.timelineTitle,
    path: '/timeline',
    lang,
    description: lang === 'it'
      ? 'Linea del tempo interattiva della storia economica: 26 teorie da Adam Smith a Piketty con formule e grafici, e 10 case history di crisi (1873, 1929, Weimar, Florida, shock petroliferi, Giappone, Asia 1997, dot-com, subprime, produttività italiana).'
      : 'Interactive economic history timeline: 26 theories from Adam Smith to Piketty with formulas and charts, and 10 crisis case studies (1873, 1929, Weimar, Florida, oil shocks, Japan, Asia 1997, dot-com, subprime, Italian productivity).',
  });

  useEffect(() => {
    const fetchTimeline = async () => {
      setLoading(true);
      try {
        const [periodsRes, articlesRes] = await Promise.all([
          axios.get(`${API}/timeline/periods`),
          axios.get(`${API}/timeline/articles${selectedPeriod ? `?period=${selectedPeriod}` : ''}`)
        ]);
        setPeriods(periodsRes.data);
        setArticles(articlesRes.data);
      } catch (err) {
        console.error("Timeline error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [selectedPeriod]);

  if (loading) return <div className="loading">{t.loading}</div>;

  const totalArticles = periods.reduce((sum, p) => sum + p.article_count, 0);

  return (
    <div className="timeline-page" data-testid="timeline-page">
      <div className="timeline-header">
        <h1 className="timeline-title">
          <Calendar size={32} />
          {t.timelineTitle}
        </h1>
        <p className="timeline-desc">{t.timelineDesc}</p>
        <div className="timeline-stats">
          <span><strong>{totalArticles}</strong> {t.articlesInPeriod}</span>
          <span>•</span>
          <span><strong>{periods.length}</strong> {lang === 'it' ? 'periodi storici' : 'historical periods'}</span>
        </div>
      </div>

      <ReadingPaths lang={lang} />

      {/* Interactive Timeline Bar */}
      <div className="timeline-container" data-testid="timeline-bar">
        <button 
          className={`timeline-period-btn ${!selectedPeriod ? 'active' : ''}`}
          onClick={() => setSelectedPeriod(null)}
          data-testid="timeline-all-btn"
        >
          <span className="period-name">{t.allPeriods}</span>
          <span className="period-years">{periods[0]?.start}-{periods[periods.length - 1]?.end}</span>
          <span className="period-count">{totalArticles}</span>
        </button>
        
        <div className="timeline-track">
          {periods.map((period, idx) => (
            <button
              key={period.code}
              className={`timeline-period ${selectedPeriod === period.code ? 'active' : ''} ${period.article_count === 0 ? 'empty' : ''}`}
              onClick={() => setSelectedPeriod(period.code)}
              style={{ 
                borderTopColor: period.color,
                '--period-color': period.color 
              }}
              data-testid={`timeline-period-${period.code}`}
            >
              <div className="period-marker" style={{ backgroundColor: period.color }}>
                {period.article_count > 0 && (
                  <span className="period-badge">{period.article_count}</span>
                )}
              </div>
              <div className="period-info">
                <span className="period-name">{lang === 'it' ? period.name_it : period.name_en}</span>
                <span className="period-years">{period.start}-{period.end}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Period Info */}
      {selectedPeriod && (
        <div className="selected-period-info" data-testid="selected-period">
          {(() => {
            const p = periods.find(x => x.code === selectedPeriod);
            if (!p) return null;
            return (
              <div className="period-header" style={{ borderLeftColor: p.color }}>
                <h2 style={{ color: p.color }}>
                  {lang === 'it' ? p.name_it : p.name_en}
                </h2>
                <span className="period-range">{p.start} — {p.end}</span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Articles for selected period */}
      <div className="timeline-articles" data-testid="timeline-articles">
        {articles.length === 0 ? (
          <div className="no-results">
            {lang === 'it' 
              ? 'Nessun articolo disponibile per questo periodo. Gli articoli storici sono in fase di raccolta ed elaborazione.'
              : 'No articles available for this period yet. Historical articles are being collected and processed.'}
          </div>
        ) : (
          articles.map(article => {
            const period = periods.find(p => p.code === article.historical_period);
            const isCurated = article.tags?.includes('curated_theory');
            const isCaseHistory = article.tags?.includes('curated_case_history');
            return (
              <div key={article.id} className={`timeline-article ${isCurated || isCaseHistory ? 'curated' : ''}`} data-testid={`timeline-article-${article.id}`}>
                <div className="timeline-article-date" style={{ backgroundColor: period?.color || '#78350f' }}>
                  {article.historical_year || '?'}
                </div>
                <div className="timeline-article-content">
                  <div className="timeline-article-meta">
                    <span className="timeline-article-period" style={{ color: period?.color }}>
                      {period ? (lang === 'it' ? period.name_it : period.name_en) : article.historical_period}
                    </span>
                    <span className="timeline-article-source">{article.source_name}</span>
                    {isCurated && (
                      <span className="curated-badge" style={{ background: period?.color }}>
                        {lang === 'it' ? 'TEORIA' : 'THEORY'}
                      </span>
                    )}
                    {isCaseHistory && (
                      <span className="curated-badge" style={{ background: '#7a4d00' }} data-testid={`case-history-badge-${article.id}`}>
                        CASE HISTORY
                      </span>
                    )}
                    {isChipFeature(article.tags) && (
                      <span className="curated-badge" style={{ background: '#b45309' }} data-testid={`chip-feature-badge-${article.id}`}>
                        {lang === 'it' ? 'GRAFICO FILIERA' : 'SUPPLY-CHAIN CHART'}
                      </span>
                    )}
                    {(isLabourFeature(article.tags) || (article.tags || []).includes('merton') || (article.tags || []).includes('great_divergence') || (article.tags || []).includes('rust_belt') || (article.tags || []).includes('econometrics_history')) && (
                      <span className="curated-badge" style={{ background: '#2f6fd6' }} data-testid={`charts-feature-badge-${article.id}`}>
                        {lang === 'it' ? 'GRAFICI INTERATTIVI' : 'INTERACTIVE CHARTS'}
                      </span>
                    )}
                  </div>
                  <Link to={`/${lang}/article/${article.id}`} className="timeline-article-title">
                    <h3>{lang === 'it' && article.title_it ? article.title_it : article.title_en}</h3>
                  </Link>
                  {isCurated && article.chart_type && (
                    <TheoryChart chartType={article.chart_type} color={period?.color || '#003366'} />
                  )}
                  {isChipFeature(article.tags) && <SupplyChainMap lang={lang} compact />}
                  {isLabourFeature(article.tags) && <EditorialCharts lang={lang} articleId={article.id} />}
                  {(article.tags || []).includes('merton') && <EditorialCharts lang={lang} articleId={article.id} set="merton" />}
                  {(article.tags || []).includes('great_divergence') && <EditorialCharts lang={lang} articleId={article.id} set="divergence" />}
                  {(article.tags || []).includes('rust_belt') && <EditorialCharts lang={lang} articleId={article.id} set="rustbelt" />}
                  {(article.tags || []).includes('econometrics_history') && <EditorialCharts lang={lang} articleId={article.id} set="econometrics" />}
                  <p className="timeline-article-summary">
                    {(lang === 'it' && article.summary_it ? article.summary_it : article.summary_en).substring(0, 300)}...
                  </p>
                  <Link to={`/${lang}/article/${article.id}`} className="read-more">
                    {t.readMore} <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Footer
const Footer = ({ lang }) => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-container">
        <div className="footer-brand">
          <span className="footer-logo">HORIZON BRIEF</span>
          <p className="footer-tagline">{TAGLINE[lang] || TAGLINE.it}</p>
        </div>
        <div className="footer-links">
          <Link to={`/${lang}/category/science`}>Science</Link>
          <Link to={`/${lang}/category/biology`}>Biology</Link>
          <Link to={`/${lang}/category/technology`}>Technology</Link>
          <Link to={`/${lang}/category/hardware`}>Hardware</Link>
          <Link to={`/${lang}/category/economics`}>Economics</Link>
          <Link to={`/${lang}/category/finance`}>Finance</Link>
          <Link to={`/${lang}/category/economic_history`}>Econ. History</Link>
          <Link to={`/${lang}/newsletter`} data-testid="footer-newsletter-link">Newsletter</Link>
          <a href={`${API}/feed.xml?lang=${lang}`} data-testid="footer-rss-link">RSS</a>
        </div>
        <div className="footer-copy">
          © {new Date().getFullYear()} Horizon Brief. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main App
const SUPPORTED_UI_LANGS = ['it', 'en'];
const detectLang = () => ((navigator.language || 'en').toLowerCase().startsWith('it') ? 'it' : 'en');

// Old links without language prefix (/article/x) -> /{lang}/article/x
const LegacyRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/${detectLang()}${location.pathname}${location.search}`} replace />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prev = useRef(pathname);
  useEffect(() => {
    const section = p => p.split('/').slice(0, 3).join('/');
    if (section(prev.current) !== section(pathname) || /\/article\//.test(pathname)) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    prev.current = pathname;
  }, [pathname]);
  return null;
};

const LangShell = () => {
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  if (!SUPPORTED_UI_LANGS.includes(lang)) {
    return <LegacyRedirect />;
  }
  const setLang = (next) => {
    if (next === lang) return;
    navigate(location.pathname.replace(/^\/(it|en)(?=\/|$)/, `/${next}`) + location.search);
  };
  return (
    <>
      <ScrollToTop />
      <Header lang={lang} setLang={setLang} />
      <main className="main-wrapper">
        <Routes>
          <Route index element={<HomePage lang={lang} />} />
          <Route path="category/:categoryId" element={<CategoryPage lang={lang} />} />
          <Route path="article/:articleId" element={<ArticlePage lang={lang} />} />
          <Route path="search" element={<SearchPage lang={lang} />} />
          <Route path="timeline" element={<TimelinePage lang={lang} />} />
          <Route path="newsletter" element={<NewsletterPage lang={lang} />} />
          <Route path="*" element={<Navigate to={`/${lang}/`} replace />} />
        </Routes>
      </main>
      <Footer lang={lang} />
    </>
  );
};

function App() {
  return (
    <div className="App" data-testid="app">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to={`/${detectLang()}/`} replace />} />
          <Route path="/:lang/*" element={<LangShell />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
